import { useEffect, useRef, useState } from "react";

const WIDGET_SRC =
  "https://services.leadconnectorhq.com/prospecting/client/widget-embed.js";
const WIDGET_ID = "6a246f4dbba3021ad1a85b4f";

interface VisibilityAuditWidgetProps {
  className?: string;
}

/**
 * Free instant visibility / website audit widget (LeadConnector prospecting).
 * Lazy-loaded via IntersectionObserver so the third-party script never blocks
 * initial render. The embed script reads its own `data-widget-id` and mounts
 * the widget inside the container it's appended to.
 */
export function VisibilityAuditWidget({ className = "" }: VisibilityAuditWidgetProps) {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const loadedRef = useRef(false);

  // Only load once the section scrolls into view.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: "300px", threshold: 0.01 }
    );

    const el = containerRef.current;
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Inject the embed script into the container once visible.
  useEffect(() => {
    if (!isVisible || loadedRef.current) return;
    const target = containerRef.current;
    if (!target) return;

    loadedRef.current = true;

    const script = document.createElement("script");
    script.src = WIDGET_SRC;
    script.async = true;
    script.dataset.widgetId = WIDGET_ID;
    target.appendChild(script);
  }, [isVisible]);

  return (
    <div
      ref={containerRef}
      // Force the injected third-party widget DOM (form / iframe) to stay
      // within the container on mobile: it ships with a fixed width + resize
      // handle that otherwise overflows the viewport.
      className={`w-full max-w-full overflow-x-hidden [&_*]:!max-w-full [&_iframe]:!w-full [&_*]:!resize-none ${className}`}
      style={{ minHeight: 120 }}
      aria-label="Free instant website and visibility audit"
    />
  );
}

export default VisibilityAuditWidget;
